import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Check, ImagePlus, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { mediaUrl } from "@/lib/site";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader } from "@/components/common";

export function useMedia() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/media").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);
  return { items, load };
}

export function UploadButton({ onDone, testId = "media-upload-input" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = [...e.target.files];
    if (!files.length) return;
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/admin/media", fd, { timeout: 120000 });
      toast.success(`${data.ids.length} image(s) ajoutée(s)`);
      onDone(data.ids);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid={testId} />
      <button type="button" className="mr-btn-primary" disabled={busy} onClick={() => ref.current.click()}><ImagePlus size={16} /> {busy ? "Envoi…" : "Téléverser des images"}</button>
    </>
  );
}

export function MediaPicker({ open, multi, selected = [], onClose, onPick }) {
  const { items, load } = useMedia();
  const [sel, setSel] = useState(selected);
  useEffect(() => {
    setSel(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const toggle = (id) => setSel(multi ? (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]) : [id]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-mr-surface border-mr-border max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="media-picker">
        <DialogHeader><DialogTitle className="font-display">Médiathèque</DialogTitle></DialogHeader>
        <div className="flex justify-between gap-3 mb-3"><UploadButton testId="picker-upload-input" onDone={(ids) => { load(); setSel(multi ? [...sel, ...ids] : [ids[0]]); }} />
          <button className="mr-btn-primary" data-testid="media-picker-confirm" onClick={() => { onPick(multi ? sel : sel[0] || ""); onClose(); }}><Check size={16} /> Valider ({sel.length})</button></div>
        {!items ? <Loader /> : (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">{items.map((m) => (
            <button key={m.id} type="button" onClick={() => toggle(m.id)} data-testid="media-picker-item" className={`relative border-2 ${sel.includes(m.id) ? "border-mr-accent" : "border-transparent"}`}>
              <img src={mediaUrl(m.id)} alt={m.alt} className="w-full aspect-square object-cover" loading="lazy" />
              {sel.includes(m.id) && <span className="absolute top-1 right-1 bg-mr-accent text-mr-on-accent rounded-full w-5 h-5 grid place-items-center text-[10px] font-bold">{multi ? sel.indexOf(m.id) + 1 : <Check size={12} />}</span>}
            </button>))}</div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ImageField({ value, multi, onChange, label }) {
  const [open, setOpen] = useState(false);
  const ids = multi ? value || [] : value ? [value] : [];
  return (
    <div><span className="mr-label">{label}</span>
      <div className="flex flex-wrap gap-2 items-center">
        {ids.map((id) => <div key={id} className="relative"><img src={mediaUrl(id)} alt="" className="w-16 h-16 object-cover border border-mr-border" />
          <button type="button" aria-label="Retirer" className="absolute -top-1.5 -right-1.5 bg-mr-raised border border-mr-border rounded-full p-0.5" onClick={() => onChange(multi ? ids.filter((x) => x !== id) : "")}><X size={10} /></button></div>)}
        <button type="button" className="mr-btn-secondary min-h-[40px] px-3" data-testid="image-field-choose" onClick={() => setOpen(true)}><ImagePlus size={14} /> {ids.length ? "Modifier" : "Choisir"}</button>
      </div>
      <MediaPicker open={open} multi={multi} selected={ids} onClose={() => setOpen(false)} onPick={onChange} />
    </div>
  );
}

const LABELS = { eyebrow: "Surtitre", title: "Titre", accent: "Mots en vert italique", body: "Texte", cta: "Bouton principal", cta2_label: "Bouton secondaire", cta2_link: "Lien du bouton secondaire",
  link: "Lien", image: "Image", images: "Images", mockup: "Maquette d'interface", name: "Nom", role: "Rôle", company: "Entreprise", sector: "Secteur", result: "Résultat",
  label: "Libellé", criteria: "Critères (un par ligne)", kind: "Type d'opération", category: "Catégorie", empty: "Texte si vide", reverse: "Image à gauche", tabs: "Afficher en onglets" };
const LONG = ["body", "result"];

export function FieldsEditor({ obj, onChange }) {
  const set = (k, v) => onChange({ ...obj, [k]: v });
  return (
    <div className="grid gap-3">
      {Object.entries(obj).filter(([k]) => k !== "type").map(([k, v]) => {
        const label = LABELS[k] || k;
        if (k === "image" || k === "images") return <ImageField key={k} label={label} value={v} multi={k === "images"} onChange={(nv) => set(k, nv)} />;
        if (typeof v === "boolean") return <label key={k} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={v} onChange={(e) => set(k, e.target.checked)} className="w-5 h-5" />{label}</label>;
        if (Array.isArray(v) && (v.length === 0 ? k === "criteria" : typeof v[0] === "string"))
          return <label key={k} className="block"><span className="mr-label">{label}</span><textarea className="mr-input min-h-[90px]" value={v.join("\n")} onChange={(e) => set(k, e.target.value.split("\n"))} /></label>;
        if (Array.isArray(v)) return <ItemsEditor key={k} label={k === "groups" ? "Groupes" : "Éléments"} items={v} onChange={(nv) => set(k, nv)} />;
        if (typeof v === "string")
          return <label key={k} className="block"><span className="mr-label">{label}</span>{LONG.includes(k) ? <textarea className="mr-input min-h-[90px]" value={v} onChange={(e) => set(k, e.target.value)} /> : <input className="mr-input" value={v} onChange={(e) => set(k, e.target.value)} />}</label>;
        return null;
      })}
    </div>
  );
}

function ItemsEditor({ label, items, onChange }) {
  const blank = items[0] ? Object.fromEntries(Object.entries(items[0]).map(([k, v]) => [k, Array.isArray(v) ? [] : typeof v === "boolean" ? false : ""])) : { title: "", body: "" };
  const move = (i, d) => { const n = [...items]; [n[i], n[i + d]] = [n[i + d], n[i]]; onChange(n); };
  return (
    <div><span className="mr-label">{label} ({items.length})</span>
      <div className="space-y-2">{items.map((it, i) => (
        <div key={i} className="border border-mr-border p-3 bg-mr-bg">
          <div className="flex justify-end gap-1 mb-2">
            <button type="button" aria-label="Monter" disabled={i === 0} onClick={() => move(i, -1)} className="mr-btn-ghost min-h-[32px] px-2"><ArrowUp size={14} /></button>
            <button type="button" aria-label="Descendre" disabled={i === items.length - 1} onClick={() => move(i, 1)} className="mr-btn-ghost min-h-[32px] px-2"><ArrowDown size={14} /></button>
            <button type="button" aria-label="Supprimer" onClick={() => onChange(items.filter((_, j) => j !== i))} className="mr-btn-ghost min-h-[32px] px-2 text-mr-danger"><Trash2 size={14} /></button>
          </div>
          <FieldsEditor obj={it} onChange={(nv) => onChange(items.map((x, j) => (j === i ? nv : x)))} />
        </div>))}</div>
      <button type="button" className="mr-btn-secondary min-h-[38px] mt-2" data-testid="items-add-button" onClick={() => onChange([...items, blank])}><Plus size={14} /> Ajouter</button>
    </div>
  );
}
